import { useCallback } from 'react';
import { MapMouseEvent } from 'mapbox-gl';
import turfNearestPointOnLine from '@turf/nearest-point-on-line';
import { lineString, type Position } from '@turf/helpers';
import { useDispatch, useSelector } from '../../../redux';
import { addActiveSegmentHandle, selectCustomRouteActiveSegment } from '../../../redux/customRoute';
import { reverseCoord } from '../../../utils/legacyGeoJSONConversions';
import { LineProperties } from '../../../types/CustomRoute';
import { customRouteActiveSegmentPolylineId } from '../../../utils/layers';

const useAddHandle = () => {
  const dispatch = useDispatch();
  const activeSegment = useSelector(selectCustomRouteActiveSegment);

  const addHandle = useCallback(
    (event: MapMouseEvent) => {
      try {
        const {
          lngLat: { lng, lat },
          features
        } = event;

        // Since the click may have been on a layer above the polyline (e.g. OSM line or chevrons), find the first feature that is on the active segment polyline
        const feature = features?.find(feature => feature.layer.id === customRouteActiveSegmentPolylineId);

        if (!feature || !activeSegment) {
          return false;
        }

        const { pathIndex } = feature.properties! as LineProperties;
        const path = activeSegment.paths[pathIndex];
        if (!path || !path.points) {
          return false;
        }

        // Apply reverseCoord and create line - use cleanCoords only if normal operation fails
        const reversedCoords = path.points.map(reverseCoord);
        const line = lineString(reversedCoords);

        let nearestPointResult;
        let replacementPoints;
        try {
          // Try normal operation first
          nearestPointResult = turfNearestPointOnLine(line, [lng, lat]);
        } catch {
          // Turf's nearestPointOnLine currently fails when there are adjacent duplicate points
          // https://github.com/Turfjs/turf/issues/2809
          // If nearestPointOnLine fails, manually remove adjacent duplicate coordinates and try again
          // Note that we do this manually rather than using turf's cleanCoords function as suggested in the issue above because cleanCoords removes more than just adjacent duplicate points and may significantly alter the geometry
          // https://turfjs.org/docs/api/cleanCoords
          // If we still see a high failure rate we can consider using cleanCoords
          const deduplicatedCoords = line.geometry.coordinates.reduce((acc, coord, index) => {
            if (index === 0 || coord[0] !== acc[acc.length - 1][0] || coord[1] !== acc[acc.length - 1][1]) {
              acc.push(coord);
            }

            return acc;
          }, [] as Position[]);
          nearestPointResult = turfNearestPointOnLine(lineString(deduplicatedCoords), [lng, lat]);

          // Since the deduplicated coordinates used to calculate the nearest point are a subset of the original coordinates, the index of the nearest point result won't be usable with the original coordinates
          // We will replace all of the points on the path with the deduplicated coordinates before adding the new handle
          replacementPoints = deduplicatedCoords.map(reverseCoord);
        }
        const point = nearestPointResult.geometry.coordinates;
        const pointIndex = nearestPointResult.properties.index;
        dispatch(
          addActiveSegmentHandle({
            pathIndex,
            pointIndex,
            latitude: point[1],
            longitude: point[0],
            replacementPoints
          })
        );
        return true;
      } catch {
        // Handle gracefully without additional logging
        // cleanCoords should prevent most coordinate errors, but keep try/catch for edge cases
        return false;
      }
    },
    [activeSegment, dispatch]
  );

  return addHandle;
};

export default useAddHandle;
